import {
  inject,
  Injectable,
  InjectionToken,
  Signal,
  signal,
} from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';

/** The Google Maps JS API key (provided by the app from the FE_-injected env). */
export const GOOGLE_MAPS_API_KEY = new InjectionToken<string>(
  'google.maps.api.key',
);

/**
 * Loads the Google Maps JS SDK once via `@googlemaps/js-api-loader` and exposes load
 * state as signals. Loading establishes `google.maps.importLibrary`, which
 * `@angular/google-maps` and `MapGeocoder` then lazy-load against — so ALL consumers
 * (the `<google-map>` and every geocoder call) must wait for `loaded`.
 *
 * The SDK language is pinned to `de` so geocoder region names are stable for the
 * ISO 3166-2 derivation (DACH map labels render in German — an accepted trade-off).
 */
@Injectable({ providedIn: 'root' })
export class MapsLoaderService {
  readonly #apiKey = inject(GOOGLE_MAPS_API_KEY);
  readonly #loaded = signal(false);
  readonly #error = signal<string | null>(null);

  readonly loaded: Signal<boolean> = this.#loaded.asReadonly();
  readonly error: Signal<string | null> = this.#error.asReadonly();

  #loadPromise: Promise<void> | null = null;

  /** Idempotent: the SDK is loaded at most once regardless of how many callers await. */
  load(): Promise<void> {
    if (!this.#loadPromise) {
      this.#loadPromise = this.#doLoad();
    }
    return this.#loadPromise;
  }

  async #doLoad(): Promise<void> {
    try {
      const loader = new Loader({
        apiKey: this.#apiKey,
        version: 'weekly',
        language: 'de',
      });
      await loader.importLibrary('maps');
      await loader.importLibrary('geocoding');
      this.#loaded.set(true);
    } catch (cause) {
      this.#error.set(
        cause instanceof Error ? cause.message : 'Failed to load Google Maps',
      );
      // Clear the cached promise so a later call can retry (e.g. transient
      // network failure) rather than re-returning the rejected promise forever.
      this.#loadPromise = null;
      throw cause;
    }
  }
}
